import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase/config';
import {
  Loader2, Server, Globe, Users, Trash2, Ban, ShieldCheck, ShieldAlert,
  Activity, Lock, MapPin, Star, Clock, AlertTriangle, CalendarDays, Gauge, Signal,
  Search, CheckCircle2, Bomb, BadgeCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const setAuthorizedFn = httpsCallable(functions, 'modSecurity_setAuthorized');
const deleteEntryFn = httpsCallable(functions, 'modSecurity_deleteEntry');
const setMainServerFn = httpsCallable(functions, 'modSecurity_setMainServer');
const setSelfDestructFn = httpsCallable(functions, 'modSecurity_setSelfDestruct');
const lockdownManyFn = httpsCallable(functions, 'modSecurity_lockdownMany');
const setVerifiedFn = httpsCallable(functions, 'modSecurity_setVerified');

// Clave de una cuenta verificada en `mod_config/verified.accounts`. Tiene que
// normalizar IGUAL que `modSecurity_setVerified` en functions.
function verifiedKey(account?: string): string {
  return String(account || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 64);
}

// Un activo se considera "online" si reportó hace menos de 60 s
// (el mod manda un latido cada 15 s).
const ONLINE_THRESHOLD_MS = 60 * 1000;

// Umbral de actividad semanal en el servidor: < 2 h/semana (o nunca ha entrado)
// → rojo; a partir de 2 h, sin problemas. (La franja ámbar de 2–4 h se quitó.)
const WEEK_RED_SECONDS = 2 * 3600;

type PlayerRef = { name?: string; uuid?: string };

type ModServer = {
  id: string;
  name?: string;
  ip?: string;
  modVersion?: string;
  mcVersion?: string;
  online?: boolean;
  playerCount?: number;
  players?: PlayerRef[];
  authorized?: boolean;
  killTitle?: string;
  killMessage?: string;
  lastSeen?: any;
  firstSeen?: any;
  startedAt?: any;
};

type ModClient = {
  id: string;
  account?: string;
  uuid?: string;
  worldName?: string;
  ip?: string;
  modVersion?: string;
  authorized?: boolean;
  killTitle?: string;
  killMessage?: string;
  lastSeen?: any;
  firstSeen?: any;
  startedAt?: any;
  totalSeconds?: number;
  days?: Record<string, number>;
};

type PlayerActivity = {
  id: string;
  name?: string;
  uuid?: string;
  totalSeconds?: number;
  days?: Record<string, number>;
};

// Objetivo de un lockdown: puede ser un servidor o un mundo privado.
// Con `ids` es un lockdown MASIVO de mundos privados (los no verificados).
type KillTarget = { id: string; name: string; collection: 'mod_servers' | 'mod_clients'; ids?: string[] };

type RiskLevel = 'never' | 'red' | 'ok';

function toMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return null;
}

function fmt(ts: any): string {
  const ms = toMs(ts);
  if (!ms) return '——';
  return new Date(ms).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function relativeFrom(ms: number | null, now: number): string {
  if (!ms) return '——';
  const diff = Math.max(0, now - ms);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

function fmtDuration(seconds?: number): string {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// Clave de día (yyyy-mm-dd) en hora de Madrid — debe coincidir con la del backend.
function madridDayKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

function lastDayKeys(n: number): string[] {
  const out: string[] = [];
  const now = Date.now();
  for (let i = 0; i < n; i++) out.push(madridDayKey(new Date(now - i * 86400000)));
  return out;
}

function prettyDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return key;
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
    weekday: 'short', day: '2-digit', month: 'short',
  });
}

// Línea de dato: "Etiqueta ········ valor"
function DataRow({ label, value, icon, tone = 'slate' }: {
  label: string; value: React.ReactNode; icon?: React.ReactNode; tone?: 'slate' | 'sky';
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {icon && <span className={tone === 'sky' ? 'text-sky-400/70' : 'text-indigo-400/70'}>{icon}</span>}
      <span className="shrink-0 text-muted-foreground/70">{label}</span>
      <span className="h-px flex-1 bg-white/8" />
      <span className="truncate font-medium text-foreground/90">{value}</span>
    </div>
  );
}

const RISK_STYLES: Record<RiskLevel, { ring: string; dot: string; label: string; chip: string }> = {
  never: {
    ring: 'border-rose-500/50 shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]',
    dot: 'bg-rose-500',
    label: 'Nunca ha entrado al servidor',
    chip: 'border-rose-500/50 bg-rose-500/15 text-rose-300',
  },
  red: {
    ring: 'border-rose-500/50 shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]',
    dot: 'bg-rose-500',
    label: 'Menos de 2 h esta semana',
    chip: 'border-rose-500/50 bg-rose-500/15 text-rose-300',
  },
  ok: {
    ring: 'border-white/10',
    dot: 'bg-emerald-400',
    label: 'Actividad correcta',
    chip: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  },
};

// Los 3 apartados en los que se reparten los mundos privados (uno encima de otro).
type ClientGroupKey = 'lockdown' | 'inactive' | 'ok';

const CLIENT_GROUPS: {
  key: ClientGroupKey;
  title: string;
  hint: string;
  tone: 'rose' | 'amber' | 'emerald';
  icon: React.ReactNode;
}[] = [
  {
    key: 'lockdown', title: 'Bajo Lockdown',
    hint: 'Bloqueados: el mod les muestra el mensaje de kill switch.',
    tone: 'rose', icon: <Lock className="h-4 w-4" />,
  },
  {
    key: 'inactive', title: 'Sin actividad o muy poca',
    hint: 'Nunca han entrado al servidor, o menos de 2 h en los últimos 7 días.',
    tone: 'rose', icon: <ShieldAlert className="h-4 w-4" />,
  },
  {
    key: 'ok', title: 'Sin problemas',
    hint: '2 h o más en el servidor durante los últimos 7 días.',
    tone: 'emerald', icon: <CheckCircle2 className="h-4 w-4" />,
  },
];

const GROUP_TONES = {
  rose: { text: 'text-rose-300', bg: 'bg-rose-500/15', border: 'border-rose-400/30', bar: 'bg-rose-500' },
  amber: { text: 'text-amber-300', bg: 'bg-amber-400/15', border: 'border-amber-400/30', bar: 'bg-amber-400' },
  emerald: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30', bar: 'bg-emerald-400' },
};

export default function AdminSeguridadModPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tab, setTab] = useState<'servers' | 'clients'>('servers');
  const [servers, setServers] = useState<ModServer[]>([]);
  const [clients, setClients] = useState<ModClient[]>([]);
  const [activity, setActivity] = useState<PlayerActivity[]>([]);
  const [mainServerId, setMainServerId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const [killTarget, setKillTarget] = useState<KillTarget | null>(null);
  const [killTitle, setKillTitle] = useState('SERVIDOR NO AUTORIZADO');
  const [killMessage, setKillMessage] = useState(
    'Este servidor no está autorizado para ejecutar Mod Cofrade. Contacta con el autor.'
  );
  const [busy, setBusy] = useState(false);
  const [historyOf, setHistoryOf] = useState<string | null>(null);
  // Cuentas de Minecraft verificadas (claves de `verifiedKey`).
  const [verified, setVerified] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet', { replace: true });
    }
  }, [user, userData, loading, navigate]);

  // Reloj en vivo (1 s) — también refresca el estado online.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const unsubS = onSnapshot(query(collection(db, 'mod_servers')), (snap) => {
      setServers(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    const unsubC = onSnapshot(query(collection(db, 'mod_clients')), (snap) => {
      setClients(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    const unsubA = onSnapshot(query(collection(db, 'mod_player_activity')), (snap) => {
      setActivity(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    const unsubCfg = onSnapshot(doc(db, 'mod_config', 'main'), (snap) => {
      setMainServerId(snap.exists() ? (snap.data().mainServerId || null) : null);
    });
    const unsubVer = onSnapshot(doc(db, 'mod_config', 'verified'), (snap) => {
      const accounts = (snap.exists() ? snap.data().accounts : null) || {};
      setVerified(new Set(Object.keys(accounts).filter((k) => accounts[k] === true)));
    });
    return () => { unsubS(); unsubC(); unsubA(); unsubCfg(); unsubVer(); };
  }, [userData?.role]);

  // Índice de actividad en el servidor por uuid y por nombre de cuenta.
  const activityIndex = useMemo(() => {
    const byUuid = new Map<string, PlayerActivity>();
    const byName = new Map<string, PlayerActivity>();
    activity.forEach((a) => {
      if (a.uuid) byUuid.set(a.uuid.toLowerCase(), a);
      if (a.name) byName.set(a.name.toLowerCase(), a);
    });
    return { byUuid, byName };
  }, [activity]);

  const weekKeys = useMemo(() => lastDayKeys(7), [
    // Se recalcula al cambiar de día (la clave de hoy cambia).
    madridDayKey(new Date(now)),
  ]);

  // Cruza cada mundo privado con su actividad en el servidor dedicado.
  const clientInfo = useMemo(() => {
    const map = new Map<string, { activity: PlayerActivity | null; weekSeconds: number; risk: RiskLevel }>();
    clients.forEach((c) => {
      const act =
        (c.uuid && activityIndex.byUuid.get(c.uuid.toLowerCase())) ||
        (c.account && activityIndex.byName.get(c.account.toLowerCase())) ||
        null;
      const days = act?.days || {};
      const weekSeconds = weekKeys.reduce((s, k) => s + Number(days[k] || 0), 0);
      let risk: RiskLevel;
      if (!act || !Number(act.totalSeconds || 0)) risk = 'never';
      else if (weekSeconds < WEEK_RED_SECONDS) risk = 'red';
      else risk = 'ok';
      map.set(c.id, { activity: act, weekSeconds, risk });
    });
    return map;
  }, [clients, activityIndex, weekKeys]);

  async function setMain(srv: ModServer) {
    setBusy(true);
    try {
      await setMainServerFn({ serverId: srv.id });
      toast({ title: 'Servidor principal', description: `${srv.name || srv.id} es ahora el servidor principal.` });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  const sortedServers = useMemo(
    () => [...servers].sort((a, b) => (toMs(b.lastSeen) || 0) - (toMs(a.lastSeen) || 0)),
    [servers]
  );

  const isOnline = (s: { lastSeen?: any }) => {
    const ms = toMs(s.lastSeen);
    return ms != null && now - ms < ONLINE_THRESHOLD_MS;
  };

  // Los mundos con problemas de actividad suben arriba; luego por último ping.
  const sortedClients = useMemo(() => {
    const rank: Record<RiskLevel, number> = { never: 0, red: 1, ok: 2 };
    return [...clients].sort((a, b) => {
      const ra = rank[clientInfo.get(a.id)?.risk || 'ok'];
      const rb = rank[clientInfo.get(b.id)?.risk || 'ok'];
      if (ra !== rb) return ra - rb;
      return (toMs(b.lastSeen) || 0) - (toMs(a.lastSeen) || 0);
    });
  }, [clients, clientInfo]);

  // Los mundos se reparten en 5 listas. El lockdown manda sobre el nivel de
  // actividad: un mundo bloqueado sale SOLO en su lista. Dentro de cada lista,
  // primero los que están activos ahora y después por último ping (más reciente
  // arriba). Se recalcula cada ~5 s para que el orden por "activo" siga vivo sin
  // reordenar en cada tic del reloj.
  const nowBucket = Math.floor(now / 5000);
  const clientGroups = useMemo(() => {
    const groups: Record<ClientGroupKey, ModClient[]> = {
      lockdown: [], inactive: [], ok: [],
    };
    clients.forEach((c) => {
      if (c.authorized === false) { groups.lockdown.push(c); return; }
      const risk = clientInfo.get(c.id)?.risk || 'ok';
      // "Nunca ha entrado" y "menos de 2 h" comparten lista; la tarjeta ya
      // distingue ambos casos con su propia etiqueta.
      if (risk === 'never' || risk === 'red') groups.inactive.push(c);
      else groups.ok.push(c);
    });
    const byActivityThenPing = (a: ModClient, b: ModClient) => {
      const oa = isOnline(a) ? 0 : 1;
      const ob = isOnline(b) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      return (toMs(b.lastSeen) || 0) - (toMs(a.lastSeen) || 0);
    };
    (Object.keys(groups) as (keyof typeof groups)[]).forEach((k) => groups[k].sort(byActivityThenPing));
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, clientInfo, nowBucket]);

  function openKillDialog(target: KillTarget, current?: { killTitle?: string; killMessage?: string }) {
    setKillTarget(target);
    const isClient = target.collection === 'mod_clients';
    setKillTitle(current?.killTitle || (isClient ? 'MUNDO NO AUTORIZADO' : 'SERVIDOR NO AUTORIZADO'));
    setKillMessage(current?.killMessage
      || 'Este equipo no está autorizado para ejecutar Mod Cofrade. Contacta con el autor.');
  }

  const isVerified = (c: ModClient) => verified.has(verifiedKey(c.account));

  // Mundos a los que alcanzaría el lockdown masivo: no verificados y no bloqueados ya.
  const unverifiedOpen = clients.filter((c) => c.authorized !== false && !isVerified(c));

  function openBulkLockdown() {
    if (unverifiedOpen.length === 0) return;
    setKillTarget({
      id: '__bulk__',
      name: `${unverifiedOpen.length} mundo${unverifiedOpen.length === 1 ? '' : 's'} privado${unverifiedOpen.length === 1 ? '' : 's'} de cuentas NO verificadas`,
      collection: 'mod_clients',
      ids: unverifiedOpen.map((c) => c.id),
    });
    setKillTitle('MUNDO NO AUTORIZADO');
    setKillMessage('Este equipo no está autorizado para ejecutar Mod Cofrade. Contacta con el autor.');
  }

  async function toggleVerified(c: ModClient) {
    if (!c.account) {
      toast({ title: 'Sin cuenta', description: 'Este mundo no reporta nombre de cuenta.', variant: 'destructive' });
      return;
    }
    const next = !isVerified(c);
    try {
      await setVerifiedFn({ account: c.account, verified: next });
      toast({ title: next ? 'Cuenta verificada' : 'Verificación retirada', description: c.account });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    }
  }

  async function applyKill(authorized: boolean) {
    if (!killTarget) return;
    setBusy(true);
    try {
      if (killTarget.ids) {
        await lockdownManyFn({ ids: killTarget.ids, killTitle, killMessage });
        toast({ title: 'Lockdown masivo activado', description: `${killTarget.ids.length} mundos. La orden llegará en ≈15 s.` });
        setKillTarget(null);
        return;
      }
      await setAuthorizedFn({ serverId: killTarget.id, collection: killTarget.collection, authorized, killTitle, killMessage });
      toast({
        title: authorized ? 'Acceso restaurado' : 'Lockdown activado',
        description: authorized
          ? 'Se levantará en el próximo latido (≈15 s).'
          : 'La orden llegará al objetivo en ≈15 s.',
      });
      setKillTarget(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo aplicar.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function reauthorize(target: KillTarget, current?: { killTitle?: string; killMessage?: string }) {
    setBusy(true);
    try {
      await setAuthorizedFn({
        serverId: target.id, collection: target.collection, authorized: true,
        killTitle: current?.killTitle || 'SERVIDOR NO AUTORIZADO',
        killMessage: current?.killMessage || '',
      });
      toast({ title: 'Acceso restaurado', description: 'Se levantará en ≈15 s.' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function removeEntry(collectionName: 'mod_servers' | 'mod_clients', id: string) {
    if (!confirm('¿Eliminar este registro? Reaparecerá si el objetivo sigue activo.')) return;
    try {
      await deleteEntryFn({ collection: collectionName, id });
      toast({ title: 'Registro eliminado' });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo eliminar.', variant: 'destructive' });
    }
  }

  // Ordena la AUTODESTRUCCIÓN del mod: se elimina de la carpeta mods del objetivo
  // (borrado real en Linux/server; renombrado a .disabled en Windows/mundo privado)
  // y se bloquea la sesión actual. Es IRREVERSIBLE en el objetivo → doble confirmación.
  async function selfDestruct(target: KillTarget) {
    const isClient = target.collection === 'mod_clients';
    const what = isClient ? 'este MUNDO PRIVADO' : 'este SERVIDOR';
    if (!confirm(
      `AUTODESTRUCCIÓN\n\nEl mod se eliminará de la carpeta mods de ${what} (${target.name}).\n` +
      `• Server (Linux): se BORRA el jar.\n• Mundo privado (Windows): se renombra a .disabled.\n\n` +
      `La orden llega en ≈15 s y surte pleno efecto al reiniciar. Es irreversible en el objetivo.\n\n¿Continuar?`)) return;
    if (!confirm(`Confirmación final: autodestruir el mod en "${target.name}". ¿Seguro?`)) return;
    setBusy(true);
    try {
      await setSelfDestructFn({ serverId: target.id, collection: target.collection, selfDestruct: true });
      toast({
        title: 'Autodestrucción ordenada',
        description: 'El mod se retirará del objetivo en ≈15 s y se bloqueará la sesión actual.',
      });
    } catch (e: any) {
      toast({ title: 'Error', description: e?.message || 'No se pudo ordenar.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full flex-col items-center justify-center gap-3">
        <ShieldCheck className="h-12 w-12 animate-pulse text-indigo-400" />
        <p className="text-sm text-muted-foreground">Verificando permisos…</p>
      </div>
    );
  }

  const onlineServers = sortedServers.filter(isOnline).length;
  const onlineClients = sortedClients.filter(isOnline).length;
  const lockedServers = sortedServers.filter((s) => s.authorized === false).length;
  const lockedClients = sortedClients.filter((c) => c.authorized === false).length;
  // Los bloqueados ya tienen su propia lista/chip → no se cuentan aquí también.
  const flaggedClients = clientGroups.inactive.length;

  const historyClient = historyOf ? clients.find((c) => c.id === historyOf) : null;

  return (
    <div className="space-y-6 pb-12">
      {/* ── Cabecera ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-500/15 via-card/40 to-card/20 p-8"
      >
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-indigo-500/40 blur-xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-indigo-400/30 bg-indigo-500/15 text-indigo-300">
                <ShieldCheck className="h-9 w-9" />
              </div>
            </div>
            <div>
              <h1 className="font-headline text-4xl font-bold">Seguridad del Mod</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                Telemetría en tiempo real de los servidores y mundos privados que ejecutan Mod Cofrade.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-xs font-medium text-emerald-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            En directo · {new Date(now).toLocaleTimeString('es-ES')}
          </div>
        </div>
      </motion.div>

      {/* ── Métricas ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          icon={<Activity className="h-5 w-5" />} label="Activos ahora"
          value={onlineServers + onlineClients} accent="emerald"
        />
        <StatTile
          icon={<Server className="h-5 w-5" />} label="Servidores"
          value={`${onlineServers}/${sortedServers.length}`} accent="indigo"
        />
        <StatTile
          icon={<Globe className="h-5 w-5" />} label="Mundos privados"
          value={`${onlineClients}/${sortedClients.length}`} accent="sky"
        />
        <StatTile
          icon={<Lock className="h-5 w-5" />} label="Bajo lockdown"
          value={lockedServers + lockedClients} accent={lockedServers + lockedClients ? 'rose' : 'slate'}
          hint={`${lockedServers} servidor(es) · ${lockedClients} mundo(s)`}
        />
      </div>

      {/* ── Pestañas ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-2xl border border-white/10 bg-card/40 p-1">
          {([
            ['servers', 'Servidores', <Server className="h-4 w-4" key="s" />, sortedServers.length],
            ['clients', 'Mundos privados', <Globe className="h-4 w-4" key="c" />, sortedClients.length],
          ] as const).map(([key, lbl, ic, count]) => (
            <button
              key={key}
              onClick={() => setTab(key as 'servers' | 'clients')}
              className={cn(
                'flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all',
                tab === key
                  ? 'bg-primary/15 text-primary shadow-sm ring-1 ring-primary/30'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {ic} {lbl}
              <span className="rounded-full bg-white/10 px-1.5 text-[0.65rem]">{count}</span>
            </button>
          ))}
        </div>
        {tab === 'clients' && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="destructive" disabled={busy || unverifiedOpen.length === 0}
              className="gap-1.5 rounded-full" onClick={openBulkLockdown}>
              <Ban className="h-3.5 w-3.5" /> Lockdown a todos los no verificados ({unverifiedOpen.length})
            </Button>
            <span className="flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-300">
              <BadgeCheck className="h-3.5 w-3.5" />
              {clients.filter(isVerified).length} verificados
            </span>
            {lockedClients > 0 && (
              <span className="flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-300">
                <Lock className="h-3.5 w-3.5" />
                {lockedClients} {lockedClients === 1 ? 'mundo bajo lockdown' : 'mundos bajo lockdown'}
              </span>
            )}
            {flaggedClients > 0 && (
              <span className="flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" />
                {flaggedClients} con poca actividad
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── SERVIDORES ── */}
      {tab === 'servers' && (
        <div className="space-y-3">
          {sortedServers.length === 0 && (
            <EmptyState icon={<Server className="h-10 w-10" />} text="Ningún servidor ha reportado todavía." />
          )}
          {sortedServers.map((srv, i) => {
            const online = isOnline(srv);
            const unauthorized = srv.authorized === false;
            const isMain = mainServerId === srv.id;
            return (
              <motion.div
                key={srv.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.25) }}
                className={cn(
                  'relative overflow-hidden rounded-2xl border bg-card/40 p-5 transition-colors',
                  unauthorized ? 'border-rose-500/50 shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]' : 'border-white/10 hover:bg-card/60',
                )}
              >
                <span className={cn(
                  'absolute inset-y-0 left-0 w-1',
                  unauthorized ? 'bg-rose-500' : online ? 'bg-emerald-400' : 'bg-white/10',
                )} />
                <div className="flex flex-wrap items-start justify-between gap-4 pl-2">
                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className={cn(
                        'grid h-9 w-9 place-items-center rounded-xl border',
                        unauthorized ? 'border-rose-400/30 bg-rose-500/15 text-rose-300' : 'border-indigo-400/30 bg-indigo-500/15 text-indigo-300',
                      )}>
                        <Server className="h-4 w-4" />
                      </div>
                      <span className="truncate text-base font-bold">{srv.name || 'Servidor'}</span>
                      <StatusPill online={online} onlineLabel="En línea" offLabel="Sin señal" />
                      {unauthorized && <Chip tone="rose" icon={<Lock className="h-3 w-3" />}>Lockdown</Chip>}
                      {isMain && <Chip tone="amber" icon={<Star className="h-3 w-3 fill-amber-300" />}>Principal</Chip>}
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <DataRow icon={<MapPin className="h-3.5 w-3.5" />} label="IP de origen" value={srv.ip || '——'} />
                      <DataRow icon={<Users className="h-3.5 w-3.5" />} label="Jugadores" value={`${srv.playerCount ?? 0} conectados`} />
                      <DataRow icon={<Gauge className="h-3.5 w-3.5" />} label="Versión" value={srv.modVersion || '——'} />
                      <DataRow icon={<Clock className="h-3.5 w-3.5" />} label="Activo desde" value={fmt(srv.startedAt)} />
                      <DataRow icon={<Signal className="h-3.5 w-3.5" />} label="Último ping" value={relativeFrom(toMs(srv.lastSeen), now)} />
                      <DataRow label="ID" value={<span className="opacity-60">{srv.id.slice(0, 18)}</span>} />
                    </div>

                    {!!srv.players?.length && (
                      <div className="flex flex-wrap gap-1.5">
                        {srv.players.map((p, j) => (
                          <span key={j} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 py-0.5 pl-0.5 pr-2.5 text-xs">
                            <img
                              src={`https://mc-heads.net/avatar/${p.uuid || p.name}/20`}
                              alt="" className="h-5 w-5 rounded-full"
                            />
                            {p.name || p.uuid}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      {unauthorized ? (
                        <Button size="sm" disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-600/85"
                          onClick={() => reauthorize({ id: srv.id, name: srv.name || srv.id, collection: 'mod_servers' }, srv)}>
                          <ShieldCheck className="h-4 w-4" /> Restaurar acceso
                        </Button>
                      ) : (
                        <Button size="sm" variant="destructive" className="gap-1.5"
                          onClick={() => openKillDialog({ id: srv.id, name: srv.name || srv.id, collection: 'mod_servers' }, srv)}>
                          <Ban className="h-4 w-4" /> Lockdown
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="text-rose-400/70 hover:bg-rose-500/10 hover:text-rose-400"
                        title="Autodestruir el mod en este servidor" disabled={busy}
                        onClick={() => selfDestruct({ id: srv.id, name: srv.name || srv.id, collection: 'mod_servers' })}>
                        <Bomb className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive"
                        onClick={() => removeEntry('mod_servers', srv.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button size="sm" variant="outline" disabled={busy || isMain} onClick={() => setMain(srv)}
                      className={cn('gap-1.5 rounded-xl', isMain ? 'border-amber-400/50 text-amber-300' : 'border-white/15')}>
                      <Star className={cn('h-3.5 w-3.5', isMain && 'fill-amber-300')} />
                      {isMain ? 'Servidor principal' : 'Marcar como principal'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── MUNDOS PRIVADOS — 5 listas independientes ── */}
      {tab === 'clients' && (
        <div>
          {sortedClients.length === 0 ? (
            <EmptyState icon={<Globe className="h-10 w-10" />} text="Ningún mundo privado ha reportado todavía." />
          ) : (
            <div className="space-y-4">
            {CLIENT_GROUPS.map((g) => (
              <ClientGroupList
                key={g.key}
                group={g}
                clients={clientGroups[g.key]}
                clientInfo={clientInfo}
                now={now}
                busy={busy}
                isOnline={isOnline}
                isVerified={isVerified}
                onToggleVerified={toggleVerified}
                onOpenHistory={setHistoryOf}
                onLockdown={(c) => openKillDialog({ id: c.id, name: c.account || c.id, collection: 'mod_clients' }, c)}
                onRestore={(c) => reauthorize({ id: c.id, name: c.account || c.id, collection: 'mod_clients' }, c)}
                onRemove={(c) => removeEntry('mod_clients', c.id)}
                onSelfDestruct={(c) => selfDestruct({ id: c.id, name: c.account || c.id, collection: 'mod_clients' })}
              />
            ))}
            </div>
          )}
        </div>
      )}

      {/* ── Historial diario de un mundo privado ── */}
      <Dialog open={!!historyClient} onOpenChange={(o) => !o && setHistoryOf(null)}>
        <DialogContent className="flex max-h-[90dvh] flex-col p-0 sm:max-w-2xl">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-3">
              <img
                src={`https://mc-heads.net/avatar/${historyClient?.uuid || historyClient?.account || 'steve'}/40`}
                alt="" className="h-10 w-10 rounded-xl border border-white/10"
              />
              <span>
                {historyClient?.account || 'Mundo privado'}
                <span className="block text-xs font-normal text-muted-foreground">
                  {historyClient?.worldName || 'Sin nombre de mundo'}
                </span>
              </span>
            </DialogTitle>
            <DialogDescription>
              Tiempo que este mundo privado ha estado abierto cada día (hora de Madrid).
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            {historyClient && <ClientHistory client={historyClient} info={clientInfo.get(historyClient.id)} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Diálogo kill switch ── */}
      <Dialog open={!!killTarget} onOpenChange={(o) => !o && setKillTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="h-5 w-5" /> Activar lockdown remoto
            </DialogTitle>
            <DialogDescription>
              Objetivo: <strong className="text-foreground">{killTarget?.name}</strong>. Al confirmar, todo el que esté
              conectado recibirá un mensaje permanente en pantalla, chat y consola. Se transmite en ≈15 s.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="killTitle">Encabezado (rojo, grande)</Label>
              <Input id="killTitle" value={killTitle} onChange={(e) => setKillTitle(e.target.value)} maxLength={80} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="killMessage">Mensaje (subtítulo + chat + consola)</Label>
              <Textarea id="killMessage" value={killMessage} rows={3} maxLength={300}
                onChange={(e) => setKillMessage(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKillTarget(null)} disabled={busy}>Cancelar</Button>
            <Button variant="destructive" disabled={busy} onClick={() => applyKill(false)} className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
              Activar lockdown
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Piezas de UI ──

const ACCENTS: Record<string, { text: string; bg: string; border: string }> = {
  emerald: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-400/30' },
  indigo: { text: 'text-indigo-300', bg: 'bg-indigo-500/15', border: 'border-indigo-400/30' },
  sky: { text: 'text-sky-300', bg: 'bg-sky-500/15', border: 'border-sky-400/30' },
  rose: { text: 'text-rose-300', bg: 'bg-rose-500/15', border: 'border-rose-400/30' },
  slate: { text: 'text-muted-foreground', bg: 'bg-white/5', border: 'border-white/10' },
};

function StatTile({ icon, label, value, accent, hint }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; accent: string; hint?: string;
}) {
  const a = ACCENTS[accent] || ACCENTS.slate;
  return (
    <div className="rounded-2xl border border-white/10 bg-card/40 p-4">
      <div className="flex items-center gap-2.5">
        <span className={cn('grid h-9 w-9 place-items-center rounded-xl border', a.bg, a.border, a.text)}>{icon}</span>
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <p className={cn('mt-2 text-3xl font-bold', a.text)}>{value}</p>
      {hint && <p className="mt-0.5 text-[0.7rem] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function StatusPill({ online, onlineLabel, offLabel }: { online: boolean; onlineLabel: string; offLabel: string }) {
  return (
    <span className={cn(
      'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold',
      online ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300' : 'border-white/10 bg-white/5 text-muted-foreground',
    )}>
      <span className={cn('h-1.5 w-1.5 rounded-full', online ? 'animate-pulse bg-emerald-400' : 'bg-muted-foreground/50')} />
      {online ? onlineLabel : offLabel}
    </span>
  );
}

function Chip({ children, tone, icon }: { children: React.ReactNode; tone: 'rose' | 'amber'; icon?: React.ReactNode }) {
  return (
    <span className={cn(
      'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold',
      tone === 'rose' ? 'border-rose-500/50 bg-rose-500/15 text-rose-300' : 'border-amber-400/50 bg-amber-400/15 text-amber-300',
    )}>
      {icon} {children}
    </span>
  );
}

// Una de las 5 listas de mundos privados: cabecera con contador, buscador propio
// y las tarjetas ya ordenadas (activos primero, luego por último ping).
function ClientGroupList({
  group, clients, clientInfo, now, busy, isOnline, isVerified, onToggleVerified,
  onOpenHistory, onLockdown, onRestore, onRemove, onSelfDestruct,
}: {
  group: { key: ClientGroupKey; title: string; hint: string; tone: 'rose' | 'amber' | 'emerald'; icon: React.ReactNode };
  clients: ModClient[];
  clientInfo: Map<string, { activity: PlayerActivity | null; weekSeconds: number; risk: RiskLevel }>;
  now: number;
  busy: boolean;
  isOnline: (s: { lastSeen?: any }) => boolean;
  isVerified: (c: ModClient) => boolean;
  onToggleVerified: (c: ModClient) => void;
  onOpenHistory: (id: string) => void;
  onLockdown: (c: ModClient) => void;
  onRestore: (c: ModClient) => void;
  onRemove: (c: ModClient) => void;
  onSelfDestruct: (c: ModClient) => void;
}) {
  const [term, setTerm] = useState('');
  const tone = GROUP_TONES[group.tone];

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => [c.account, c.worldName, c.ip, c.uuid, c.modVersion]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [clients, term]);

  const onlineCount = clients.filter(isOnline).length;

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-card/25">
      <div className="space-y-2.5 border-b border-white/8 px-3 py-3">
        <div className="flex items-center gap-2.5">
          <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-xl border', tone.bg, tone.border, tone.text)}>
            {group.icon}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-bold">{group.title}</span>
              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold', tone.bg, tone.text)}>
                {clients.length}
              </span>
            </div>
            {onlineCount > 0 && (
              <span className="flex items-center gap-1 text-[0.7rem] text-emerald-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                {onlineCount} activo{onlineCount === 1 ? '' : 's'} ahora
              </span>
            )}
          </div>
        </div>
        <p className="text-[0.7rem] leading-snug text-muted-foreground">{group.hint}</p>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar..."
            className="h-9 pl-8 text-sm"
          />
        </div>
      </div>

      <div className="p-2.5">
        {clients.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">No hay mundos en esta lista.</p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Ninguno coincide con «{term}».
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filtered.map((c, i) => (
            <ClientCard
              key={c.id}
              client={c}
              index={i}
              info={clientInfo.get(c.id)}
              online={isOnline(c)}
              now={now}
              busy={busy}
              verified={isVerified(c)}
              onToggleVerified={onToggleVerified}
              onOpenHistory={onOpenHistory}
              onLockdown={onLockdown}
              onRestore={onRestore}
              onRemove={onRemove}
              onSelfDestruct={onSelfDestruct}
            />
          ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientCard({
  client: c, index, info, online, now, busy, verified, onToggleVerified,
  onOpenHistory, onLockdown, onRestore, onRemove, onSelfDestruct,
}: {
  client: ModClient;
  index: number;
  info?: { activity: PlayerActivity | null; weekSeconds: number; risk: RiskLevel };
  online: boolean;
  now: number;
  busy: boolean;
  verified: boolean;
  onToggleVerified: (c: ModClient) => void;
  onOpenHistory: (id: string) => void;
  onLockdown: (c: ModClient) => void;
  onRestore: (c: ModClient) => void;
  onRemove: (c: ModClient) => void;
  onSelfDestruct: (c: ModClient) => void;
}) {
  const unauthorized = c.authorized === false;
  const risk = info?.risk || 'ok';
  const rs = RISK_STYLES[risk];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.2) }}
      onClick={() => onOpenHistory(c.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpenHistory(c.id); }}
      className={cn(
        'relative cursor-pointer space-y-2.5 overflow-hidden rounded-xl border bg-card/40 p-3 pl-4 transition-colors hover:bg-card/60',
        unauthorized ? 'border-rose-500/50 shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]' : rs.ring,
      )}
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', unauthorized ? 'bg-rose-500' : rs.dot)} />

      <div className="flex items-center gap-2">
        <img
          src={`https://mc-heads.net/avatar/${c.uuid || c.account || 'steve'}/32`}
          alt="" className="h-8 w-8 shrink-0 rounded-lg border border-white/10"
        />
        <span className="flex min-w-0 flex-1 items-center gap-1 text-sm font-bold">
          <span className="truncate">{c.account || 'Cuenta desconocida'}</span>
          {verified && <BadgeCheck className="h-4 w-4 shrink-0 fill-sky-500 text-white" aria-label="Verificado" />}
        </span>
        <StatusPill online={online} onlineLabel="Jugando" offLabel="Cerrado" />
      </div>

      {/* Señalamos explícitamente el caso "nunca ha entrado", que comparte lista
          con los de muy poca actividad. */}
      {(unauthorized || risk !== 'ok') && (
        <div className="flex flex-wrap gap-1.5">
          {unauthorized && <Chip tone="rose" icon={<Lock className="h-3 w-3" />}>Lockdown</Chip>}
          {risk !== 'ok' && (
            <span className={cn('flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.62rem] font-semibold', rs.chip)}>
              <ShieldAlert className="h-3 w-3 shrink-0" /> {rs.label}
            </span>
          )}
        </div>
      )}

      <div className="grid gap-1.5">
        <DataRow tone="sky" icon={<Globe className="h-3.5 w-3.5" />} label="Mundo" value={c.worldName || '——'} />
        <DataRow tone="sky" icon={<MapPin className="h-3.5 w-3.5" />} label="IP" value={c.ip || '——'} />
        <DataRow tone="sky" icon={<Clock className="h-3.5 w-3.5" />} label="Abierto" value={fmtDuration(c.totalSeconds)} />
        <DataRow tone="sky" icon={<Activity className="h-3.5 w-3.5" />} label="Servidor 7d"
          value={
            <span className={cn(risk === 'ok' ? 'text-emerald-300' : 'text-rose-300')}>
              {info?.activity ? fmtDuration(info.weekSeconds) : 'Nunca'}
            </span>
          } />
        <DataRow tone="sky" icon={<Signal className="h-3.5 w-3.5" />} label="Ping" value={relativeFrom(toMs(c.lastSeen), now)} />
      </div>

      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" disabled={busy}
          className={cn('h-8 w-8 shrink-0', verified ? 'text-sky-400 hover:bg-sky-500/10' : 'text-muted-foreground hover:text-sky-400')}
          title={verified ? 'Quitar verificación' : 'Verificar esta cuenta'} onClick={() => onToggleVerified(c)}>
          <BadgeCheck className="h-4 w-4" />
        </Button>
        {unauthorized ? (
          <Button size="sm" disabled={busy} className="h-8 flex-1 gap-1.5 bg-emerald-600 text-xs hover:bg-emerald-600/85"
            onClick={() => onRestore(c)}>
            <ShieldCheck className="h-3.5 w-3.5" /> Restaurar
          </Button>
        ) : (
          <Button size="sm" variant="destructive" className="h-8 flex-1 gap-1.5 text-xs" onClick={() => onLockdown(c)}>
            <Ban className="h-3.5 w-3.5" /> Lockdown
          </Button>
        )}
        <Button size="icon" variant="ghost" disabled={busy}
          className="h-8 w-8 shrink-0 text-rose-400/70 hover:bg-rose-500/10 hover:text-rose-400"
          title="Autodestruir el mod en este mundo" onClick={() => onSelfDestruct(c)}>
          <Bomb className="h-3.5 w-3.5" />
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(c)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </motion.div>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/10 bg-card/20 py-16 text-muted-foreground">
      {icon}
      <p className="text-sm">{text}</p>
    </div>
  );
}

// Historial diario del mundo privado + su actividad en el servidor dedicado.
function ClientHistory({ client, info }: {
  client: ModClient;
  info?: { activity: PlayerActivity | null; weekSeconds: number; risk: RiskLevel };
}) {
  const days = client.days || {};
  const entries = Object.entries(days)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => (a[0] < b[0] ? 1 : -1));
  const max = entries.reduce((m, [, v]) => Math.max(m, Number(v)), 0);

  const serverDays = info?.activity?.days || {};
  const risk = info?.risk || 'ok';
  const rs = RISK_STYLES[risk];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-white/10 bg-muted/30 p-4">
          <p className="text-xs text-muted-foreground">Total mundo abierto</p>
          <p className="mt-1 text-2xl font-bold">{fmtDuration(client.totalSeconds)}</p>
        </div>
        <div className={cn('rounded-2xl border bg-muted/30 p-4', rs.ring)}>
          <p className="text-xs text-muted-foreground">En el servidor (últimos 7 días)</p>
          <p className={cn('mt-1 text-2xl font-bold', risk === 'ok' ? 'text-emerald-300' : 'text-rose-300')}>
            {info?.activity ? fmtDuration(info.weekSeconds) : 'Nunca'}
          </p>
          {risk !== 'ok' && <p className="mt-0.5 text-[0.7rem] text-muted-foreground">{rs.label}</p>}
        </div>
      </div>

      <div>
        <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <CalendarDays className="h-4 w-4 text-sky-400" /> Actividad por día
        </h4>
        {entries.length === 0 ? (
          <p className="rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-muted-foreground">
            Todavía no hay historial registrado para este mundo.
          </p>
        ) : (
          <div className="space-y-1.5">
            <AnimatePresence initial={false}>
              {entries.map(([key, value]) => {
                const secs = Number(value);
                const serverSecs = Number(serverDays[key] || 0);
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="rounded-xl border border-white/8 bg-muted/20 p-2.5"
                  >
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="font-medium capitalize">{prettyDay(key)}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-sky-300">{fmtDuration(secs)} en su mundo</span>
                        <span className={serverSecs > 0 ? 'text-emerald-300' : 'text-muted-foreground/60'}>
                          {serverSecs > 0 ? `${fmtDuration(serverSecs)} en el servidor` : 'sin servidor'}
                        </span>
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/5">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-sky-500 to-indigo-400"
                        style={{ width: `${max ? Math.max(4, (secs / max) * 100) : 0}%` }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
